import Vue from 'vue';
import { searchAll } from '@/services/searchService';
import SearchResultsDropdown from './SearchResultsDropdown.vue';
export default Vue.extend({
    name: 'GlobalSearch',
    components: {
        SearchResultsDropdown
    },
    props: {
        sessions: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            searchQuery: '',
            searchResults: [],
            totalMatches: 0,
            sessionResultsCount: 0,
            showDropdown: false,
            selectedIndex: -1,
            debounceTimer: null,
            // localStorage constants
            SEARCH_STORAGE_KEY: 'sparkconverse_globalSearch',
            SEARCH_TTL_MS: 30 * 60 * 1000, // 30 minutes
        };
    },
    mounted() {
        console.log('[GlobalSearch] Component mounted');
        // Load saved search state from localStorage
        this.loadSearchState();
    },
    watch: {
        searchQuery(newVal) {
            // Save search state to localStorage on every change
            this.saveSearchState();
        },
        sessions: {
            handler(newSessions) {
                // If we have a saved search query and sessions just loaded, re-trigger search
                if (this.searchQuery && this.searchQuery.length >= 2 && newSessions && newSessions.length > 0) {
                    console.log('[GlobalSearch] Sessions updated, re-triggering search if needed');
                    // Small delay to let Vue update
                    this.$nextTick(() => {
                        if (this.searchResults.length === 0) {
                            console.log('[GlobalSearch] Re-performing search after sessions loaded');
                            this.performSearch();
                            this.showDropdown = true;
                        }
                    });
                }
            },
            deep: false
        }
    },
    methods: {
        /**
         * Save search state to localStorage with timestamp
         */
        saveSearchState() {
            try {
                const state = {
                    query: this.searchQuery,
                    timestamp: Date.now(),
                };
                localStorage.setItem(this.SEARCH_STORAGE_KEY, JSON.stringify(state));
                console.log('[GlobalSearch] Saved search state:', state);
            }
            catch (e) {
                console.error('[GlobalSearch] Failed to save search state:', e);
            }
        },
        /**
         * Load search state from localStorage (with TTL check)
         */
        loadSearchState() {
            try {
                const saved = localStorage.getItem(this.SEARCH_STORAGE_KEY);
                if (!saved) {
                    console.log('[GlobalSearch] No saved search state');
                    return;
                }
                const state = JSON.parse(saved);
                const age = Date.now() - state.timestamp;
                console.log('[GlobalSearch] Found saved search:', state, `(age: ${Math.round(age / 1000)}s)`);
                if (age < this.SEARCH_TTL_MS) {
                    // Valid saved search - restore it
                    this.searchQuery = state.query;
                    console.log('[GlobalSearch] Restored search query:', state.query);
                    // Trigger search if query is valid
                    if (state.query && state.query.length >= 2) {
                        // Use a longer delay to ensure sessions are loaded
                        setTimeout(() => {
                            console.log('[GlobalSearch] Performing search with restored query');
                            this.performSearch();
                            this.showDropdown = true;
                            console.log('[GlobalSearch] Re-triggered search with restored query');
                        }, 500); // Increased delay to ensure data is loaded
                    }
                }
                else {
                    // Expired - clear it
                    console.log('[GlobalSearch] Search state expired (TTL: 30 mins), clearing');
                    localStorage.removeItem(this.SEARCH_STORAGE_KEY);
                }
            }
            catch (e) {
                console.error('[GlobalSearch] Failed to load search state:', e);
                // Corrupted data - clear it
                localStorage.removeItem(this.SEARCH_STORAGE_KEY);
            }
        },
        handleInput() {
            // Debounce search - wait 300ms after user stops typing
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            this.debounceTimer = window.setTimeout(() => {
                this.performSearch();
            }, 300);
        },
        performSearch() {
            const query = this.searchQuery.trim();
            // Minimum 2 characters required
            if (query.length < 2) {
                this.searchResults = [];
                this.showDropdown = false;
                this.selectedIndex = -1;
                return;
            }
            // Perform search using search service
            // Searches ALL sessions, categories, and projects
            const results = searchAll(query, this.sessions, {
                maxResults: 15,
                includeCategories: true,
                includeProjects: true
            });
            this.searchResults = results;
            this.totalMatches = results._totalMatches || results.length;
            this.sessionResultsCount = results._sessionResultsCount || 0;
            this.showDropdown = true;
            this.selectedIndex = -1;
        },
        handleFocus() {
            if (this.searchQuery.length >= 2 && this.searchResults.length > 0) {
                this.showDropdown = true;
            }
        },
        handleKeyDown(event) {
            if (!this.showDropdown || this.searchResults.length === 0) {
                return;
            }
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedIndex = Math.min(this.selectedIndex + 1, this.searchResults.length - 1);
                    this.scrollSelectedIntoView();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
                    this.scrollSelectedIntoView();
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedIndex >= 0 && this.selectedIndex < this.searchResults.length) {
                        this.handleResultClick(this.searchResults[this.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.closeDropdown();
                    break;
                case 'Tab':
                    this.closeDropdown();
                    break;
            }
        },
        scrollSelectedIntoView() {
            this.$nextTick(() => {
                const dropdown = this.$el.querySelector('.results-list');
                const selectedRow = this.$el.querySelectorAll('.search-result-row')[this.selectedIndex];
                if (dropdown && selectedRow) {
                    const dropdownRect = dropdown.getBoundingClientRect();
                    const rowRect = selectedRow.getBoundingClientRect();
                    if (rowRect.bottom > dropdownRect.bottom) {
                        selectedRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                    else if (rowRect.top < dropdownRect.top) {
                        selectedRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                }
            });
        },
        handleResultClick(result) {
            // Close dropdown ONLY when navigating to a session (opening new page)
            // Keep dropdown open for project/category navigation (same page)
            if (result.type === 'session' && result.id) {
                // Keep search query but dropdown will re-open when user comes back
                this.$emit('navigate-to-session', result.id);
            }
            else if (result.type === 'category') {
                // Keep dropdown open for same-page navigation
                this.$emit('navigate-to-category', result.project, result.category);
            }
            else if (result.type === 'project') {
                // Keep dropdown open for same-page navigation
                this.$emit('navigate-to-project', result.project);
            }
        },
        handleProjectClick(payload) {
            // Close dropdown when navigating to project
            this.closeDropdown();
            // Navigate to project and optionally highlight session
            this.$emit('navigate-to-project-with-session', payload);
        },
        handleCategoryClick(payload) {
            // Close dropdown when navigating to category
            this.closeDropdown();
            // Navigate to category and optionally highlight session
            this.$emit('navigate-to-category-with-session', payload);
        },
        closeDropdown() {
            this.showDropdown = false;
            this.selectedIndex = -1;
        },
        clearSearch() {
            this.searchQuery = '';
            this.searchResults = [];
            this.showDropdown = false;
            this.selectedIndex = -1;
            // Clear localStorage
            localStorage.removeItem(this.SEARCH_STORAGE_KEY);
            console.log('[GlobalSearch] Cleared search state');
            this.$refs.searchInput?.focus();
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                const handler = (e) => {
                    if (!el.contains(e.target)) {
                        binding.value();
                    }
                };
                el._clickOutsideHandler = handler;
                document.addEventListener('click', handler);
            },
            unbind(el) {
                if (el._clickOutsideHandler) {
                    document.removeEventListener('click', el._clickOutsideHandler);
                    delete el._clickOutsideHandler;
                }
            }
        }
    }
});
